import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout,
  Typography,
  Button,
  Card,
  Row,
  Col,
  Tag,
  Space,
  Input,
  Empty,
  Spin,
  message,
  Popconfirm,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  RobotOutlined,
  SearchOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  CopyOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { AgentSummary, AgentListResponse, BUILDER_API, EXECUTOR_API, ModelProvider } from '../types';
import { deleteAgent } from '../services/api.service';
import { useUser } from '../contexts/UserContext';
import { colors } from '../theme';
import EntityCard from './common/EntityCard';

const { Content } = Layout;
const { Text } = Typography;

const PROVIDER_COLORS: Record<ModelProvider, string> = {
  vertex_ai: 'green',
  openai: 'blue',
  anthropic: 'purple',
};

const PROVIDER_LABELS: Record<ModelProvider, string> = {
  vertex_ai: 'Vertex AI',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
};

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const AgentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, isAdmin, email } = useUser();
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BUILDER_API}/agent/builder/agents`);
      if (res.ok) {
        const data = (await res.json()) as AgentListResponse;
        setAgents(Array.isArray(data.agents) ? data.agents : []);
      } else {
        setAgents([]);
      }
    } catch {
      message.error('No se pudo conectar con el servidor. Asegurate de que el backend esta corriendo en el puerto 8000.');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleDelete = async (agentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(agentId);
    try {
      await deleteAgent(agentId);
      setAgents((prev) => prev.filter((a) => a.id !== agentId));
      message.success('Agente eliminado');
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Error al eliminar el agente');
    } finally {
      setDeletingId(null);
    }
  };

  const canDeleteAgent = (agent: AgentSummary): boolean => {
    if (isAdmin) return true;
    if (!email || !agent.createdBy) return false;
    return agent.createdBy.toLowerCase() === email.toLowerCase();
  };

  const filteredAgents = agents.filter((agent) => {
    const term = searchTerm.toLowerCase();
    return (
      agent.name.toLowerCase().includes(term) ||
      agent.role.toLowerCase().includes(term) ||
      agent.task.toLowerCase().includes(term) ||
      agent.description.toLowerCase().includes(term)
    );
  });

  return (
    <Layout style={{ minHeight: '100vh', background: 'transparent' }}>

      <Content style={{ padding: '32px', maxWidth: 1280, margin: '0 auto', width: '100%' }}>
        {/* Search */}
        <div style={{ marginBottom: 24 }}>
          <Input
            placeholder="Buscar agentes por nombre, rol, tarea..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            size="large"
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ maxWidth: 480, borderRadius: 8 }}
          />
        </div>

        {/* Agent grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: '#8c8c8c' }}>Cargando agentes...</div>
          </div>
        ) : filteredAgents.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              agents.length === 0
                ? 'No hay agentes creados todavia'
                : 'No se encontraron agentes con ese filtro'
            }
            style={{ padding: '80px 0' }}
          >
            {agents.length === 0 && canEdit && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/builder/new')}
              >
                Crear primer agente
              </Button>
            )}
          </Empty>
        ) : (
          <Row gutter={[24, 24]}>
            {canEdit && (
              <Col xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  bordered={false}
                  onClick={() => navigate('/builder/new')}
                  style={{
                    borderRadius: 10,
                    height: '100%',
                    minHeight: 160,
                    border: '2px dashed #d9d9d9',
                    background: '#fafafa',
                    cursor: 'pointer',
                  }}
                  styles={{
                    body: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      padding: 16,
                      gap: 8,
                    },
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 10,
                      background: colors.primarySoft,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <PlusOutlined style={{ fontSize: 18, color: colors.primary }} />
                  </div>
                  <Text style={{ color: colors.primary, fontSize: 13, fontWeight: 600 }}>
                    Nuevo agente
                  </Text>
                  <Text style={{ color: '#8c8c8c', fontSize: 11, textAlign: 'center' }}>
                    Desde cero o plantilla
                  </Text>
                </Card>
              </Col>
            )}
            {filteredAgents.map((agent) => (
              <Col xs={24} sm={12} md={8} lg={6} key={agent.id}>
                <EntityCard
                  icon={agent.isMarketplace ? <ShopOutlined /> : <RobotOutlined />}
                  iconBackground={agent.isMarketplace ? '#f5f3ff' : colors.primarySoft}
                  iconColor={agent.isMarketplace ? '#7c3aed' : colors.primary}
                  title={agent.name}
                  subtitle={agent.role}
                  description={agent.description}
                  onClick={() => navigate(agent.cognitiveSpec ? `/agents/${agent.id}` : `/builder/${agent.id}`)}
                  tags={
                    <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
                      {agent.isMarketplace && (
                        <Tag color="purple" style={{ margin: 0, fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>
                          MP
                        </Tag>
                      )}
                      <Tag color={PROVIDER_COLORS[agent.modelProvider]} style={{ margin: 0, fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>
                        {PROVIDER_LABELS[agent.modelProvider]}
                      </Tag>
                    </Space>
                  }
                  footer={
                    <>
                      {/* Webhook URL */}
                      <div
                        style={{
                          marginBottom: 12,
                          padding: '8px 12px',
                          background: '#f5f5f5',
                          borderRadius: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <LinkOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
                        <Input
                          size="small"
                          value={`${EXECUTOR_API}/agent/builder/webhook/${agent.id}`}
                          readOnly
                          style={{
                            fontSize: 11,
                            fontFamily: 'monospace',
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            color: '#595959',
                          }}
                          suffix={
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(`${EXECUTOR_API}/agent/builder/webhook/${agent.id}`);
                                message.success('Webhook URL copiado');
                              }}
                              style={{ padding: '0 4px', height: 20 }}
                            />
                          }
                        />
                      </div>

                      {/* Footer */}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingTop: 12,
                          borderTop: '1px solid #f5f5f5',
                        }}
                      >
                        <Space size={4} onClick={(e) => e.stopPropagation()}>
                          <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace', letterSpacing: '0.02em' }}>
                            {agent.id.slice(0, 8)}…
                          </Text>
                          <Tooltip title="Copiar ID">
                            <CopyOutlined
                              style={{ fontSize: 11, color: '#bfbfbf', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(agent.id);
                                message.success('ID copiado');
                              }}
                            />
                          </Tooltip>
                        </Space>
                        <Space size={8} onClick={(e) => e.stopPropagation()}>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {formatDate(agent.updatedAt)}
                          </Text>
                          {canDeleteAgent(agent) && (
                            <Popconfirm
                              title="¿Eliminar este agente?"
                              description="Esta acción no se puede deshacer."
                              okText="Eliminar"
                              cancelText="Cancelar"
                              okButtonProps={{ danger: true }}
                              onConfirm={(e) => handleDelete(agent.id, e as unknown as React.MouseEvent)}
                              onPopupClick={(e) => e.stopPropagation()}
                            >
                              <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                loading={deletingId === agent.id}
                                onClick={(e) => e.stopPropagation()}
                                style={{ padding: '0 4px', height: 20 }}
                              />
                            </Popconfirm>
                          )}
                        </Space>
                      </div>
                    </>
                  }
                />
              </Col>
            ))}
          </Row>
        )}
      </Content>
    </Layout>
  );
};

export default AgentListPage;
